/**
 * Generic reorder utility function
 * Eliminates code duplication for reordering logic across entities
 */
export function reorderArrayItems<T extends { order?: number }>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number,
): T[] {
  // Sort by current order first
  items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Move item from source to destination
  const [movedItem] = items.splice(sourceIndex, 1);
  items.splice(destinationIndex, 0, movedItem);

  // Update order for all items
  items.forEach((item, index) => {
    item.order = index;
  });

  return items;
}

/**
 * Generic find and update utility
 * Eliminates code duplication for find-by-id and update patterns
 */
export function findAndUpdateById<T>(
  items: T[],
  id: string,
  idField: keyof T,
  updates: Partial<T>,
): T[] {
  const itemIndex = items.findIndex((item) => item[idField] === id);
  if (itemIndex !== -1) {
    items[itemIndex] = {
      ...items[itemIndex],
      ...updates,
    };
  }
  return items;
}

/**
 * Generic remove and reorder utility
 * Eliminates code duplication for remove-by-id and reorder patterns
 */
export function removeByIdAndReorder<T extends { order?: number }>(
  items: T[],
  id: string,
  idField: keyof T,
): T[] {
  const filtered = items.filter((item) => item[idField] !== id);

  // Update order for remaining items
  return filtered.map((item, index) => ({
    ...item,
    order: index,
  }));
}

/**
 * Generic add with order utility
 * Eliminates code duplication for adding items with automatic ordering
 */
export function addItemWithOrder<T extends { order?: number }>(
  items: T[],
  newItem: T,
): T[] {
  const newOrder = items.length;
  items.push({
    ...newItem,
    order: newOrder,
  });
  return items;
}
